import React, { useCallback, useEffect, useRef, useState } from 'react';
import { crmService } from '../../services/crm.service';
import { crmErrorMessage } from '../../utils/crm';
import {
  SIDES,
  followUpText,
  isOverdue,
  localDateString,
  lostReasonLabel,
  nextStepText,
  sideNoun,
  sinceText,
  stageLabel,
  stagesFor,
} from '../../utils/crmPipeline';
import { cardName } from './LeadRow';
import ConvertDialog from './ConvertDialog';

const VIEWS = [
  { value: 'ACTIVE', label: 'Being worked' },
  { value: 'LOST', label: 'Lost' },
  { value: 'WON', label: 'Won' },
];

const CARD_CLASS = { WON: 'crm-card-won', LOST: 'crm-card-lost' };

/**
 * The board for one side: a column per stage. A lead moves either by picking a stage on its card
 * (keyboard- and phone-friendly) or by dragging the card to another column. On the final stage,
 * open cards get a "Convert to transaction" button; won cards link to the created transaction.
 */
const PipelineBoard = ({
  book, ownerUserId, reloadKey, onOpen, onChanged, onOpenTransaction, initialView = 'ACTIVE',
}) => {
  const [side, setSide] = useState('BUY');
  const [view, setView] = useState(initialView);
  const [state, setState] = useState({ status: 'loading', data: null, error: '' });
  const [moveError, setMoveError] = useState('');
  const [dragOver, setDragOver] = useState(null);
  const [converting, setConverting] = useState(null); // { card, person } | { card, loading: true }
  const dragCardIdRef = useRef(null);
  const today = localDateString();

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, status: 'loading', error: '' }));
    (async () => {
      try {
        const data = await crmService.getPipeline(book, side, { view, ownerUserId });
        if (!cancelled) setState({ status: 'ready', data, error: '' });
      } catch (err) {
        if (!cancelled) setState({ status: 'error', data: null, error: crmErrorMessage(err) });
      }
    })();
    return () => { cancelled = true; };
  }, [book, ownerUserId, side, view, reloadKey]);

  const move = useCallback(async (card, stage) => {
    if (card.stage === stage) return;
    setMoveError('');
    try {
      await crmService.updateLead(book, card.personId, side, { stage }, ownerUserId);
      onChanged();
    } catch (err) {
      setMoveError(crmErrorMessage(err));
    }
  }, [book, side, ownerUserId, onChanged]);

  const openConvert = useCallback(async (card) => {
    setMoveError('');
    setConverting({ card, loading: true });
    try {
      const person = await crmService.getPerson(book, card.personId, ownerUserId);
      setConverting({ card, person });
    } catch (err) {
      setConverting(null);
      setMoveError(crmErrorMessage(err));
    }
  }, [book, ownerUserId]);

  const stages = state.data?.stages || stagesFor(side);
  const cards = state.data?.cards || [];
  const counts = state.data?.counts || {};
  const movable = view === 'ACTIVE';
  const finalStage = stages[stages.length - 1];

  const onCardDragStart = (card) => (event) => {
    if (!movable) return;
    dragCardIdRef.current = card.personId;
    try { event.dataTransfer.effectAllowed = 'move'; } catch (_) { /* jsdom: dataTransfer may be read-only */ }
  };
  const onCardDragEnd = () => { dragCardIdRef.current = null; setDragOver(null); };
  const onColDragOver = (stage) => (event) => {
    if (!dragCardIdRef.current) return;
    event.preventDefault();
    try { event.dataTransfer.dropEffect = 'move'; } catch (_) { /* read-only in jsdom */ }
    if (dragOver !== stage) setDragOver(stage);
  };
  const onColDragLeave = (stage) => () => { if (dragOver === stage) setDragOver(null); };
  const onColDrop = (stage) => (event) => {
    event.preventDefault();
    const id = dragCardIdRef.current;
    dragCardIdRef.current = null;
    setDragOver(null);
    if (!id) return;
    const card = cards.find((c) => c.personId === id);
    if (card && card.stage !== stage) move(card, stage);
  };

  return (
    <div className="crm-board">
      <div className="crm-board-toolbar">
        <div className="crm-segmented" role="tablist" aria-label="Pipeline side">
          {SIDES.map((s) => (
            <button
              key={s.value}
              type="button"
              role="tab"
              aria-selected={side === s.value}
              className={`crm-seg ${side === s.value ? 'active' : ''}`}
              onClick={() => setSide(s.value)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <select className="crm-select" aria-label="Show leads" value={view} onChange={(e) => setView(e.target.value)}>
          {VIEWS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
        </select>
        <span className="crm-note crm-board-counts">
          {(counts.OPEN || 0) + (counts.NURTURING || 0)} being worked · {counts.WON || 0} won · {counts.LOST || 0} lost
        </span>
      </div>

      {state.status === 'error' && <p className="crm-error" role="alert">{state.error}</p>}
      {moveError && <p className="crm-error" role="alert">{moveError}</p>}
      {state.status === 'loading' && !state.data && <p className="crm-empty">Loading…</p>}
      {state.data && cards.length === 0 && (
        <p className="crm-empty">{view === 'ACTIVE' ? `No ${sideNoun(side)}s in your pipeline yet.` : `No ${view === 'WON' ? 'won' : 'lost'} ${sideNoun(side)}s.`}</p>
      )}
      {state.data?.truncated && <p className="crm-note">Only the first part of a very large pipeline is shown.</p>}

      {state.data && cards.length > 0 && (
        <div className="crm-columns">
          {stages.map((stage) => {
            const inStage = cards.filter((c) => c.stage === stage);
            const over = dragOver === stage;
            return (
              <section
                key={stage}
                className={`crm-column ${over ? 'crm-column-over' : ''}`}
                aria-label={stageLabel(stage)}
                onDragOver={onColDragOver(stage)}
                onDragLeave={onColDragLeave(stage)}
                onDrop={onColDrop(stage)}
              >
                <h3>{stageLabel(stage)} <span className="crm-count">{inStage.length}</span></h3>
                {inStage.map((card) => {
                  const isFinal = stage === finalStage;
                  const isOpen = card.status === 'OPEN' || card.status === 'NURTURING';
                  const canConvert = movable && isOpen && isFinal && !card.transactionId;
                  const toneClass = CARD_CLASS[card.status] || '';
                  return (
                    <article
                      key={card.personId}
                      className={`crm-card ${toneClass}`}
                      draggable={movable}
                      onDragStart={onCardDragStart(card)}
                      onDragEnd={onCardDragEnd}
                    >
                      <button type="button" className="crm-link" onClick={() => onOpen(card.personId)}>{cardName(card)}</button>
                      {card.summary && <div className="crm-lead-summary">{card.summary}</div>}
                      {card.status === 'OPEN' && <div className="crm-next-action">Next: {nextStepText(card.nextTask, card.stage, today)}</div>}
                      {card.status === 'NURTURING' && <div className="crm-lead-meta"><span>Nurturing</span></div>}
                      {card.status === 'LOST' && <div className="crm-lead-meta"><span>{lostReasonLabel(card.lostReason)}</span></div>}
                      {card.nextFollowUpDate && (
                        <div className={`crm-lead-meta ${isOverdue(card.nextFollowUpDate, today) ? 'crm-overdue' : ''}`}>
                          Follow up: {followUpText(card.nextFollowUpDate, today)}
                        </div>
                      )}
                      <div className="crm-lead-meta">{card.lastContactedAt ? `Last contact ${sinceText(card.lastContactedAt)}` : 'Not contacted yet'}</div>
                      {movable && (
                        <select
                          className="crm-select crm-move"
                          aria-label={`Move ${cardName(card)} to another stage`}
                          value={card.stage}
                          onChange={(e) => move(card, e.target.value)}
                        >
                          {stages.map((s) => <option key={s} value={s}>{stageLabel(s)}</option>)}
                        </select>
                      )}
                      {canConvert && (
                        <button
                          type="button"
                          className="crm-btn crm-btn-primary crm-convert-btn"
                          onClick={() => openConvert(card)}
                          disabled={!!converting}
                        >
                          Convert to transaction
                        </button>
                      )}
                      {card.status === 'WON' && card.transactionId && onOpenTransaction && (
                        <button
                          type="button"
                          className="crm-link crm-open-transaction"
                          onClick={() => onOpenTransaction(card.transactionId)}
                        >
                          Open transaction →
                        </button>
                      )}
                    </article>
                  );
                })}
              </section>
            );
          })}
        </div>
      )}

      {converting && converting.loading && (
        <p className="crm-empty" role="status">Preparing conversion…</p>
      )}
      {converting && converting.person && (
        <ConvertDialog
          book={book}
          ownerUserId={ownerUserId}
          person={converting.person}
          side={side}
          onClose={() => setConverting(null)}
          onOpenTransaction={(id) => { setConverting(null); if (onOpenTransaction) onOpenTransaction(id); }}
          onConverted={() => { onChanged(); }}
        />
      )}
    </div>
  );
};

export default PipelineBoard;
